import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import * as React from 'react'
import {useMemo} from 'react'

import EntityPath from '../../../containers/EntityPath'
import {useBuildStatusIcon} from '../../../hooks/useBuildStatusIcon'
import {useIsAutodetectTimeZone} from '../../../hooks/useIsAutodetectTimeZone'
import {useDistanceToNow} from '../../../hooks/useTimeSelector'
import {PlaceIdList} from '../../../plugins/plugins.types'
import type {BuildStatus} from '../../../types'
import {useBuildOvertimeInfo} from '../../../utils/buildOvertime'
import {displayDate, formatDuration} from '../../../utils/dateTimeUtils'
import BuildChangesDropdown from '../../packages/Changes/BuildChangesDropdown/BuildChangesDropdown'
import BuildAgentLink from '../AgentLink/BuildAgentLink.container'
import BuildBranch from '../BranchLabel/BuildBranchLabel.container'
import BuildArtifacts from '../BuildArtifacts/BuildArtifacts.container'
import BuildNumberContainer from '../BuildNumber/BuildNumber.container'
import BuildQueueInfo from '../BuildQueueInfo/BuildQueueInfo.container'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import Plugin from '../Plugin/Plugin'
import RouterLinkWrapper from '../RouterLinkWrapper/RouterLinkWrapper'

import type {Props} from './BuildInfo.types'

import styles from './BuildInfo.css'

type StatusLinkProps = {
  icon: string
  status: BuildStatus
  statusText: string
  href: string
}
const StatusLink = React.memo(({icon, status, statusText, href}: StatusLinkProps) => (
  <RouterLinkWrapper>
    <Link
      href={href}
      className={classNames(styles.status, {
        [styles.successStatus]: status === 'SUCCESS',
        [styles.failureStatus]: status === 'FAILURE',
      })}
      data-test="build-status-link"
    >
      <Icon className={styles.icon} name={icon} />
      {statusText}
    </Link>
  </RouterLinkWrapper>
))
StatusLink.displayName = 'StatusLink'

function BuildInfo({
  buildId,
  buildTypeId,
  className,
  startDate,
  finishDate,
  inlineQueueInfo,
  duration,
  left,
  progress,
  running,
  status,
  statusText,
  href,
  elementRef,
  hideProgress,
  withPath,
  projectOrBuildTypeFilter,
  dependenciesRange,
  groupOwnQueueReasons,
}: Props) {
  const icon = useBuildStatusIcon(buildId)
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const overtimeInfo = useBuildOvertimeInfo(buildId)

  const started = useMemo(
    () => (
      <span className={styles.started}>
        {startDate != null && `Started at ${displayDate(startDate, isAutodetectTimeZone, true)}`}
      </span>
    ),
    [startDate, isAutodetectTimeZone],
  )
  const finishedFromNow = useDistanceToNow(finishDate)
  return (
    <div className={classNames(styles.buildInfo, className)}>
      {withPath === true && (
        <H3 className={styles.path}>
          <EntityPath buildTypeId={buildTypeId} projectOrBuildTypeNode={projectOrBuildTypeFilter} />
        </H3>
      )}
      {hideProgress !== true && running && left != null && progress != null && (
        <div
          className={classNames(styles.progress, {
            [styles.failing]: status !== 'SUCCESS',
          })}
          style={{
            width: `${progress}%`,
          }}
        />
      )}
      <div ref={elementRef} className={styles.infoWrapper}>
        <div className={styles.head}>
          <span className={styles.duration}>
            {overtimeInfo.isOvertime && <Icon name="overtime" className={styles.overtimeIcon} />}
            <span data-test="build-duration">
              {left != null ? `${formatDuration(left)} left` : formatDuration(duration)}
            </span>
            {overtimeInfo.isOvertime && (
              <span data-test="build-overtime">
                {`, ${formatDuration(overtimeInfo.seconds, {
                  alwaysShowSeconds: true,
                })} overtime`}
              </span>
            )}

            {!running && finishedFromNow != null && (
              <span className={styles.finishedCompact}>{finishedFromNow}</span>
            )}
          </span>
          <BuildChangesDropdown className={styles.changes} buildId={buildId} />
          <BuildArtifacts buildId={buildId} />
        </div>
        <StatusLink icon={icon} status={status} statusText={statusText} href={href} />
        <div className={styles.details}>
          <div>
            <div className={styles.timeInQueue}>
              {inlineQueueInfo !== true && <div className={styles.queueCircle} />}
              <BuildQueueInfo
                buildId={buildId}
                inline={inlineQueueInfo}
                aside={inlineQueueInfo === true && started}
                dependenciesRange={dependenciesRange}
                groupOwnReasons={groupOwnQueueReasons}
                withIcons={groupOwnQueueReasons}
              />
            </div>
            {inlineQueueInfo !== true && started}
            <Plugin
              placeId={PlaceIdList.SAKURA_BUILD_CONFIGURATION_TREND_CARD}
              buildTypeId={buildTypeId}
              buildId={buildId}
            />
          </div>
          <BuildAgentLink buildId={buildId} />
          <div className={styles.footer}>
            <div className={styles.number}>
              <BuildNumberContainer buildId={buildId} showEmptyStar withLink />
            </div>
            <BuildBranch className={styles.branch} buildId={buildId} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BuildInfo
